import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ProfileDropdown from './ProfileDropdown';
import NotificationBell from '../notifications/NotificationBell';
import ChangelogBadge from '../changelog/ChangelogBadge';
import { useAuth } from '../../context/AuthContext';
import { useChangelog } from '../../hooks/useChangelog';

const {
  FiMenu, FiX, FiMusic, FiCompass, FiGrid,
  FiUser, FiLogIn, FiZap, FiHome, FiCalendar, FiBarChart2,
} = FiIcons;

const NAV_LINKS = [
  { label: 'Home', to: '/', icon: FiHome },
  { label: 'Discover', to: '/discover', icon: FiCompass },
  { label: 'For Artists', to: '/artists', icon: FiMusic },
];

const Navbar = () => {
  const { user, profile } = useAuth();
  const { hasNewUpdate, openChangelog } = useChangelog();
  const navigate   = useNavigate();
  const location   = useLocation();
  const [scrolled,  setScrolled]  = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', handler, { passive: true });
    handler();
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => setMobileOpen(false), [location.pathname]);

  const isActive = (to) => location.pathname === to;

  const dashboardRoute =
    profile?.role === 'platform_admin' ? '/admin'
    : profile?.role === 'label_admin'  ? '/label-admin'
    : profile?.role === 'artist'       ? '/dashboard'
    : null;

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      scrolled ? 'py-3' : 'py-5'
    }`}>
      <div className={`mx-4 md:mx-8 rounded-2xl transition-all duration-500 ${
        scrolled
          ? 'glass-strong shadow-[0_8px_40px_rgba(0,0,0,0.6)]'
          : 'glass-panel'
      }`}>
        <div className="flex items-center justify-between px-5 py-3">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative">
              <img
                src="https://media-manager-c.questera.ai/greta-media/5fb2f9d5255c375c4c593c40a3604cfd971c9f56c7725c3a05c28f383b843f2c3e4f4687d5d18e728c639d20e7900999/images/aW1hZ2UvcG5n/e291f38d345a3c31aa9db040447e5035.png"
                alt="Vision Direct Logo"
                width="32"
                height="32"
                decoding="async"
                fetchPriority="high"
                className="h-8 w-8 object-contain drop-shadow-[0_0_8px_rgba(255,102,204,0.5)] grayscale group-hover:grayscale-0 transition-all duration-500"
              />
            </div>
            <span className="text-xl font-black tracking-tighter text-white uppercase italic hidden sm:block">
              Vision Direct
            </span>
          </Link>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map(({ label, to, icon }) => (
              <Link key={to} to={to}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
                  isActive(to)
                    ? 'text-white bg-white/8 border border-white/10 shadow-lg'
                    : 'text-zinc-400 hover:text-white hover:bg-white/5'
                }`}>
                <SafeIcon icon={icon} className="text-sm" />
                {label}
              </Link>
            ))}

            {dashboardRoute && (
              <Link to={dashboardRoute}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
                  isActive(dashboardRoute)
                    ? 'text-white bg-white/8 border border-white/10 shadow-lg'
                    : 'text-zinc-400 hover:text-white hover:bg-white/5'
                }`}>
                <SafeIcon icon={FiGrid} className="text-sm" />
                Dashboard
              </Link>
            )}
          </div>

          {/* Right section */}
          <div className="flex items-center gap-2">

            {/* What's New badge — desktop only */}
            <div className="hidden md:block">
              <ChangelogBadge hasNewUpdate={hasNewUpdate} onClick={openChangelog} />
            </div>

            {user ? (
              <>
                <NotificationBell userId={user.id} />
                <ProfileDropdown user={user} profile={profile} />
              </>
            ) : (
              <button
                onClick={() => navigate('/auth')}
                className="hidden md:flex items-center gap-2 px-5 py-2.5 btn-brand rounded-2xl text-xs"
              >
                <SafeIcon icon={FiLogIn} className="text-sm" /> Sign In
              </button>
            )}

            {/* Mobile menu toggle */}
            <button
              onClick={() => setMobileOpen(o => !o)}
              className="md:hidden w-10 h-10 glass-card rounded-xl flex items-center justify-center text-zinc-400 hover:text-white transition-all"
            >
              <SafeIcon icon={mobileOpen ? FiX : FiMenu} className="text-lg" />
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden border-t border-white/5 px-5 py-4 space-y-1 animate-slide-up">
            {NAV_LINKS.map(({ label, to, icon }) => (
              <Link key={to} to={to}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-black uppercase tracking-widest text-zinc-400 hover:text-white hover:bg-white/5 transition-all">
                <SafeIcon icon={icon} className="text-base" /> {label}
              </Link>
            ))}
            {dashboardRoute && (
              <Link to={dashboardRoute}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-black uppercase tracking-widest text-zinc-400 hover:text-white hover:bg-white/5 transition-all">
                <SafeIcon icon={FiGrid} className="text-base" /> Dashboard
              </Link>
            )}
            {/* What's New — mobile */}
            {hasNewUpdate && (
              <button onClick={openChangelog}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-black uppercase tracking-widest text-brand-pink hover:bg-brand-pink/10 transition-all w-full">
                <SafeIcon icon={FiZap} className="text-base" /> What's New
              </button>
            )}
            {!user && (
              <button onClick={() => navigate('/auth')}
                className="w-full mt-2 flex items-center justify-center gap-2 py-3 btn-brand rounded-2xl text-xs">
                <SafeIcon icon={FiLogIn} /> Sign In
              </button>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;